// Schema.org JSON-LD builders for rich snippets / SERP rich results.
// We produce LocalBusiness (site-wide), Service, Article and BreadcrumbList.
//
// References:
// - https://schema.org/LocalBusiness
// - https://schema.org/TaxiService
// - https://schema.org/Service
// - https://schema.org/Article
// - https://schema.org/BreadcrumbList
//
// IMPORTANT — Google policy:
// We deliberately omit aggregateRating to avoid penalty risk from fabricated
// review signals. Real ratings should only be added once Google Business
// Profile is connected and verified.

import { BUSINESS } from "./business";

const SITE_URL = "https://ambelite.com";

const ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "85A rue du Puits d'Ordet Nord",
  postalCode: "73490",
  addressLocality: "La Ravoire",
  addressRegion: "Savoie",
  addressCountry: "FR",
};

const AREA_SERVED = [
  "French Riviera",
  "Côte d'Azur",
  "Nice",
  "Cannes",
  "Monaco",
  "Monte-Carlo",
  "Saint-Tropez",
  "Saint-Jean-Cap-Ferrat",
  "Antibes",
  "Èze",
  "Villefranche-sur-Mer",
  "Beaulieu-sur-Mer",
  "Menton",
  "Mougins",
  "Cap d'Antibes",
  "Beausoleil",
  "Roquebrune-Cap-Martin",
  "Saint-Paul-de-Vence",
  "Vence",
  "Mandelieu-la-Napoule",
  "Théoule-sur-Mer",
].map((name) => ({ "@type": "AdministrativeArea", name }));

/**
 * Site-wide LocalBusiness schema. Use on every public page.
 * Mixed type LocalBusiness + TaxiService for best coverage.
 */
export function buildLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "TaxiService"],
    "@id": SITE_URL + "/#business",
    name: BUSINESS.brand,
    alternateName: ["AMB Élite", "French Riviera Transfert by AMB Élite"],
    description:
      "VIP private chauffeur service on the French Riviera. Nice Airport transfers, hourly hire, weddings, Monaco, Cannes, Saint-Tropez. Mercedes V-Class, S-Class and E-Class fleet, 24/7 service, reply within 4 hours.",
    url: SITE_URL,
    image: BUSINESS.ogImage,
    logo: BUSINESS.ogImage,
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "reservations",
        email: BUSINESS.email,
        telephone: BUSINESS.phone,
        availableLanguage: ["French", "English", "Italian", "German", "Spanish", "Arabic"],
      },
      {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: BUSINESS.emailContact,
        telephone: BUSINESS.phone,
        availableLanguage: ["French", "English", "Italian", "German", "Spanish", "Arabic"],
      },
    ],
    priceRange: "€€€€",
    currenciesAccepted: "EUR",
    paymentAccepted: "Bank transfer, Credit card, Wire transfer",
    address: ADDRESS,
    areaServed: AREA_SERVED,
    serviceArea: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: 43.6961,
        longitude: 7.2719, // Nice
      },
      geoRadius: "200000", // 200 km — covers Riviera + Italian border + Provence
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "00:00",
        closes: "23:59",
      },
    ],
    availableLanguage: ["French", "English", "Italian", "German", "Spanish", "Arabic"],
    sameAs: [BUSINESS.instagram],
    knowsAbout: [
      "Private chauffeur",
      "Airport transfer",
      "Nice Côte d'Azur Airport",
      "Cannes Film Festival transport",
      "Monaco Grand Prix transport",
      "Monaco Yacht Show transport",
      "Wedding motorcade",
      "Hourly chauffeur hire",
      "Mercedes-Benz fleet",
      "Business aviation tarmac pickup",
    ],
  };
}

/**
 * Service catalog schema — emitted from the Home / Services page.
 * Each ItemListElement is an Offer/Service variant.
 */
export function buildServiceCatalogSchema() {
  const services = [
    {
      name: "Nice Airport transfer (NCE)",
      description:
        "Private transfer from Nice Côte d'Azur Airport to Nice, Cannes, Monaco, Saint-Tropez, Cap-Ferrat in a Mercedes V-Class, S-Class or E-Class.",
    },
    {
      name: "Hourly chauffeur hire",
      description:
        "Mercedes private chauffeur by the hour — 4h, 6h, 8h, full day or multi-day, on the French Riviera and across France.",
    },
    {
      name: "Wedding motorcade",
      description:
        "Mercedes wedding motorcade up to 10 vehicles on the French Riviera. Coordinated arrivals and protocol drop-offs.",
    },
    {
      name: "Long-distance transfers",
      description:
        "Long-distance transfers from the French Riviera to Paris, Geneva, Milan, Turin, Barcelona, Genoa, Lake Como, Marseille.",
    },
    {
      name: "VIP events & business aviation",
      description:
        "Cannes Film Festival, Monaco Grand Prix, Monaco Yacht Show, MIPIM. Tarmac pickup at Sky Valet / Landmark FBO at NCE.",
    },
  ];

  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "@id": SITE_URL + "/#catalog",
    name: "French Riviera Transfert services",
    provider: { "@id": SITE_URL + "/#business" },
    itemListElement: services.map((s, i) => ({
      "@type": "Offer",
      position: i + 1,
      itemOffered: {
        "@type": "Service",
        name: s.name,
        description: s.description,
        provider: { "@id": SITE_URL + "/#business" },
        areaServed: AREA_SERVED,
      },
    })),
  };
}

/**
 * Article schema for a single blog post.
 *
 * @param {object} args
 * @param {object} args.post — raw post object from blogPosts.js
 * @param {object} args.content — localized post content (i18n[lang])
 * @param {string} args.lang
 * @param {string} args.siteUrl
 */
export function buildArticleSchema({ post, content, lang, siteUrl }) {
  const url = `${siteUrl}/blog/${post.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": url + "#article",
    headline: content.title,
    description: content.excerpt,
    image: [post.cover],
    inLanguage: lang === "fr" ? "fr-FR" : lang === "it" ? "it-IT" : lang === "de" ? "de-DE" : "en-GB",
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: {
      "@type": "Organization",
      name: "French Riviera Transfert",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "French Riviera Transfert",
      logo: { "@type": "ImageObject", url: BUSINESS.ogImage },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: "Travel · French Riviera · Private chauffeur",
    keywords: content.seo.keywords,
  };
}

/**
 * BreadcrumbList schema for blog post pages.
 */
export function buildBreadcrumbSchema({ items }) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function buildFAQSchema(items = []) {
  // FAQPage schema for Google rich snippets. The answer text strips HTML tags
  // to comply with Schema.org `acceptedAnswer.text` recommendation.
  const stripHtml = (s) =>
    (s || "")
      .replace(/<[^>]+>/g, "")
      .replace(/\s+/g, " ")
      .trim();
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtml(it.a),
      },
    })),
  };
}

